import { Button, Card, EmptyState, Icon, SegmentedControl, Table, type Column } from "@helmr/ui";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listMembers, type ListStatus, type MemberRow } from "../../api/members";
import { NoAccess } from "../../app/NoAccess";
import { areaAllows } from "../../app/nav";
import { addDays } from "../../lib/dates";
import { BulkFreezeDrawer } from "./BulkFreezeDrawer";
import { Chips, PersonCell, Problem, RowsSkeleton, dayMonthYear, useArea } from "./common";
import { EXPIRING_LEAD_DAYS, rowChips } from "./status";
import { t } from "./strings";
import s from "./members.module.css";

type Filter = "all" | "active" | "expiring" | "frozen" | "expired";
const L = t.list;

export function MembersPage() {
  const { client, ctx, today, scope } = useArea();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [picked, setPicked] = useState<Map<string, { planId: string; name: string }>>(new Map());
  const [closure, setClosure] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setQ(text.trim()), 250);
    return () => clearTimeout(id);
  }, [text]);

  const canAdd = ctx.has("members.add");
    // Bulk freeze is branch-wide: the API refuses an own-scoped role, so selection and closure are not offered.
  const grants = ctx.me?.grants;
  const canClose = ctx.has("members.freeze") && !(grants && "scope" in grants && grants.scope.clients === "own");
  const status: ListStatus | undefined = filter === "active" || filter === "frozen" || filter === "expired" ? filter : undefined;
  const expiringBefore = filter === "expiring" ? addDays(today, EXPIRING_LEAD_DAYS) : undefined;

  const query = useInfiniteQuery({
    queryKey: ["members", ...scope, q, filter, expiringBefore ?? ""],
    queryFn: ({ pageParam, signal }) => listMembers(client, { q, status, expiringBefore, cursor: pageParam }, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: ctx.status !== "loading",
    retry: false,
  });

  const rows = useMemo(() => query.data?.pages.flatMap((p) => p.members) ?? [], [query.data]);
  const branchName = useMemo(() => new Map((ctx.me?.branches ?? []).map((b) => [b.id, b.name])), [ctx.me]);

  if (ctx.status !== "loading" && !areaAllows("members", ctx.has)) return <NoAccess />;

  const togglePick = (m: MemberRow) =>
    setPicked((prev) => {
      const next = new Map(prev);
      if (next.has(m.id)) next.delete(m.id);
      else if (m.currentPlan) next.set(m.id, { planId: m.currentPlan.id, name: m.name });
      return next;
    });

  const columns: Column<MemberRow>[] = [
    ...(canClose
      ? [{
          key: "pick", header: L.columns.pick, srOnly: true,
          cell: (m: MemberRow) => (
            <input
              type="checkbox" className={s.pick} aria-label={`Select ${m.name}`} checked={picked.has(m.id)}
              disabled={!m.currentPlan || m.currentPlan.status === "expired"}
              onClick={(e) => e.stopPropagation()} onChange={() => togglePick(m)}
            />
          ),
        } satisfies Column<MemberRow>]
      : []),
    { key: "name", header: L.columns.name, cell: (m) => <PersonCell id={m.id} name={m.name} to={`/members/${m.id}`} /> },
    { key: "phone", header: L.columns.phone, cell: (m) => <span className={s.mono}>{m.phone}</span> },
    { key: "branch", header: L.columns.branch, cell: (m) => <span className={s.trunc} title={branchName.get(m.homeBranchId ?? "") ?? ""}>{branchName.get(m.homeBranchId ?? "") ?? "—"}</span> },
    {
      key: "plan", header: L.columns.plan,
      cell: (m) => m.currentPlan
        ? <><span className={s.trunc} title={m.currentPlan.name ?? ""}>{m.currentPlan.name ?? "Membership"}</span><span className={s.sub}>{m.currentPlan.status === "expired" ? "Ended" : "Until"} {dayMonthYear(m.currentPlan.endDate, ctx.locale)}</span></>
        : <span className={s.muted}>—</span>,
    },
    { key: "status", header: L.columns.status, cell: (m) => <Chips chips={rowChips(m.currentPlan, today)} /> },
  ];

  const filters = (["all", "active", "expiring", "frozen", "expired"] as const).map((v) => ({ value: v, label: L.filters[v] }));
  const noFilters = q === "" && filter === "all";

  return (
    <div className={s.page}>
      <div className={s.head}>
        <div>
          <h1 tabIndex={-1}>{L.title}</h1>
          <p>{L.intro}</p>
        </div>
        <div className={s.headActs}>
          {canClose ? <Button onClick={() => setClosure(true)}>{picked.size > 0 ? L.freezeSelected : L.closure}</Button> : null}
          {canAdd ? <Button as={Link} to="/members/new" variant="primary"><Icon name="plus" />{L.add}</Button> : null}
        </div>
      </div>

      <Card>
        <div className={s.bar}>
          <label className={s.search}>
            <Icon name="search" />
            <span className="sr-only">{L.searchLabel}</span>
            <input type="search" value={text} onChange={(e) => setText(e.target.value)} placeholder={L.searchPlaceholder} autoComplete="off" />
          </label>
          <SegmentedControl label={L.filterLabel} options={filters} value={filter} onChange={setFilter} />
          {picked.size > 0 ? <span className={s.hint}>{L.selected(picked.size)} · <button type="button" className={s.back} style={{ marginBottom: 0 }} onClick={() => setPicked(new Map())}>Clear</button></span> : null}
        </div>

        {query.isPending ? (
          <RowsSkeleton label="Loading members" />
        ) : query.error && rows.length === 0 ? (
          <Problem error={query.error} title={L.errorTitle} onRetry={() => void query.refetch()} />
        ) : rows.length === 0 ? (
          noFilters ? (
            <EmptyState icon="users" title={L.emptyTitle} description={L.emptyBody} action={canAdd ? <Button as={Link} to="/members/new" variant="primary">{L.add}</Button> : undefined} />
          ) : (
            <EmptyState icon="search" title={L.noMatchTitle} description={L.noMatchBody} />
          )
        ) : (
          <Table
            label="Members" columns={columns} rows={rows} rowKey={(m) => m.id} minWidth={720}
            onRowClick={(m) => navigate(`/members/${m.id}`)}
            hasMore={query.hasNextPage} onLoadMore={() => void query.fetchNextPage()} loadingMore={query.isFetchingNextPage}
            summary={L.summary(rows.length, query.hasNextPage)}
          />
        )}
        {query.isFetchNextPageError ? <Problem error={query.error} title={L.errorTitle} onRetry={() => void query.fetchNextPage()} /> : null}
      </Card>

      {closure ? (
        <BulkFreezeDrawer
          open onOpenChange={(o) => { if (!o) setClosure(false); }}
          picked={[...picked].map(([memberId, v]) => ({ memberId, ...v }))}
          onDone={() => setPicked(new Map())}
        />
      ) : null}
    </div>
  );
}
